<template>
  <banners
    :sticky-top="true"
    :narrow="true"
  >
    <Layout
      title="My Loads"
      :loads="loads"
      :tabs="tabs"
      :currentTab="currentTab"
      :handleTabSelection="handleTabSelection"
      :isDesktop="isDesktop"
      :isLoading="isLoading"
      :isLoadsRefetching="isLoadsRefetching"
      @search-loads="searchLoads"
      @fetch-more="fetchMoreLoads"
      @select-page="selectPage"
    >
    </Layout>
  </banners>
</template>

<script>
import { ref, computed, onBeforeMount, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Banners from '@/components/Banners.vue';
import Layout from '@/components/loads/Layout.vue';
import {
  loadSortQuery,
  loadFilterQuery,
  loadQuery,
} from '../../utils/loadFilters.js';
import { useScreenSize } from '@/composables/useScreenSize';

export default {
  name: 'My Loads List View',
  components: {
    Banners,
    Layout,
  },
  data() {
    return {
      tabs: [
        {
          title: 'dispatch',
        },
        {
          title: 'transit',
        },
        {
          title: 'completed',
        },
      ],
    };
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const { screenSize } = useScreenSize();
    const currentTab = ref('dispatch');

    const isLoadsRefetching = ref(false);

    const isLoading = computed(() => store.state.loads?.isLoadsLoading);

    const isDesktop = computed(() => screenSize.value === 'desktop');

    const currentPage = computed(
      () => store.state.loads?.[`${currentTab.value}_pagination`]?.currentPage
    );

    const totalPages = computed(
      () => store.state.loads?.[`${currentTab.value}_pagination`]?.totalPages
    );

    const handleTabSelection = async (tab) => {
      const params = router.currentRoute?.value?.query;

      let queries = { ...params };
      if (params?.equipment && !Array.isArray(params?.equipment)) {
        queries.equipment = [params.equipment];
      }

      if (currentTab.value === 'dispatch') {
        await store.commit('loads/setDispatchParams', queries);
      } else if (currentTab.value === 'transit') {
        await store.commit('loads/setTransitParams', queries);
      } else {
        await store.commit('loads/setCompletedParams', queries);
      }

      currentTab.value = tab;

      const existingQuery = store.state.loads?.[`existing_${tab}_query`];

      const query = existingQuery || loadQuery[tab];

      // fetch loads or stored state if re-selected
      await store.dispatch('loads/getLoads', {
        query,
        status: tab,
        page: currentPage?.value,
        useExisting: true,
      });

      const newParams = store.state.loads[`${tab}_params`];

      router.push({
        query: {
          ...newParams,
          view: tab,
        },
      });
    };

    const getLoads = async ({
      sort = 'start_stop_scheduled_window_started_at+asc',
      query = '',
      refetch = false,
      page = 1,
    }) => {
      try {
        await store.dispatch('loads/getLoads', {
          query: `${loadQuery[currentTab.value]}${query}`,
          sort,
          refetch,
          page,
          status: currentTab.value,
        });
      } catch (error) {
        console.error('Error fetching loads: ', error);
      } finally {
        store.commit('loads/setLoading', false);
      }
    };

    const addFiltersFromUrl = (values) => {
      let sort = 'start_stop_scheduled_window_started_at+asc';

      const filters = Object.entries(values).reduce((acc, [key, value]) => {
        if (key === 'sort') {
          sort = loadSortQuery[value]?.query;
        } else if (key === 'equipment') {
          const param = loadFilterQuery[key];

          value.forEach((eq) => {
            acc += `${param?.query}=${eq}`;
          });
        } else if (key === 'view' || key === 'search') {
          // noop
        } else {
          const param = loadFilterQuery[key];
          acc += `${param?.query}=${value}`;
        }
        return acc;
      }, '');

      getLoads({ sort, query: filters });
    };

    onBeforeMount(async () => {
      const carrier_id = store.state.users?.currentUser?.company?.carrier_id;
      const existingQuery = router.currentRoute?.value?.query;
      const existingSort = existingQuery?.sort
        ? loadSortQuery[existingQuery?.sort]?.query
        : '';

      const queryView = existingQuery?.view || 'dispatch';

      if (Object.entries(existingQuery)?.length > 0 && !existingQuery?.view) {
        let queries = { ...existingQuery };
        if (
          existingQuery?.equipment &&
          !Array.isArray(existingQuery?.equipment)
        ) {
          queries.equipment = [existingQuery.equipment];
        }

        addFiltersFromUrl(queries);
      } else {
        await store.dispatch('loads/getLoads', {
          query: loadQuery[queryView],
          sort: existingSort,
          status: queryView,
        });

        currentTab.value = queryView;
      }
      await store.dispatch('loads/getCarrierVehicles', carrier_id);

      await router.push({
        query: {
          ...existingQuery,
          view: queryView,
        },
      });
    });

    watch(
      () => router.currentRoute.value.query,
      (newValue, oldValue) => {
        if (!oldValue?.view || newValue?.view !== oldValue?.view) {
          // noop

          if (newValue?.sort && oldValue.sort !== newValue.sort) {
            addFiltersFromUrl(newValue);
          }
        } else {
          let queries = { ...newValue };
          if (newValue?.equipment && !Array.isArray(newValue?.equipment)) {
            queries.equipment = [newValue.equipment];
          }

          addFiltersFromUrl(queries);
        }
      }
    );

    const loads = computed(() => store.state?.loads?.loads);

    const searchLoads = async (term) => {
      await store.dispatch('loads/getLoads', {
        query: `${loadQuery[currentTab.value]}&q[single_external_load_number_equals]=${term}`,
        status: currentTab.value,
      });
    };

    const fetchMoreLoads = async () => {
      if (!isLoading.value && !isDesktop?.value) {
        console.log('Hit infinite scroll');
        if (currentPage?.value !== totalPages?.value) {
          isLoadsRefetching.value = true;

          // re-fetch loads based on page. Add to existing array
          await getLoads({
            page: +currentPage.value + 1,
            refetch: true,
          });

          isLoadsRefetching.value = false;
        }
      }
    };

    const selectPage = async (page) => {
      if (page != currentPage?.value) {
        await getLoads({
          page,
        });
      }
    };

    return {
      loads,
      currentTab,
      isDesktop,
      handleTabSelection,
      isLoading,
      isLoadsRefetching,
      searchLoads,
      fetchMoreLoads,
      selectPage,
    };
  },
};
</script>
